import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import QRCode from 'qrcode.react';
import { registerPasskey, isPlatformAuthenticatorAvailable, isPasskeySupported, createQRSession, pollQRSession } from '../services/passkeyService';

export const PasskeySetup = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [deviceName, setDeviceName] = useState('');
  const [isSupported, setIsSupported] = useState(false);
  const [hasPlatformAuth, setHasPlatformAuth] = useState(false);
  const [setupMethod, setSetupMethod] = useState(null); // null, 'this-device', 'qr-code'
  const [qrData, setQrData] = useState(null);
  const [qrPolling, setQrPolling] = useState(false);

  useEffect(() => {
    checkSupport();
  }, []);

  const checkSupport = async () => {
    const supported = isPasskeySupported();
    const platformAuth = await isPlatformAuthenticatorAvailable();
    setIsSupported(supported);
    setHasPlatformAuth(platformAuth);
  };

  const handleSetup = async () => {
    try {
      setLoading(true);
      setError(null);

      await registerPasskey(deviceName || null);

      setSuccess(true);
      setTimeout(() => {
        navigate('/profile');
      }, 2000);
    } catch (err) {
      console.error('Passkey setup error:', err);
      setError(err.message || 'Failed to set up passkey. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleQRSetup = async () => {
    try {
      setLoading(true);
      setError(null);

      // Start QR session for adding passkey to existing user
      const session = await createQRSession('add-passkey');
      // session.qrData contains the stringified data needed for the phone
      const qrUrl = `${window.location.origin}/passkey-qr-auth?qr=${encodeURIComponent(session.qrData)}`;
      setQrData(qrUrl);
      setQrPolling(true);

      // Poll for completion
      const pollInterval = setInterval(async () => {
        try {
          const status = await pollQRSession(session.sessionId);
          if (status.status === 'completed') {
            clearInterval(pollInterval);
            setQrPolling(false);
            setSuccess(true);
            setTimeout(() => {
              navigate('/profile');
            }, 2000);
          } else if (status.status === 'failed') {
            clearInterval(pollInterval);
            setQrPolling(false);
            setError('Failed to add passkey from phone. Please try again.');
          }
        } catch (err) {
          console.error('QR poll error:', err);
        }
      }, 2000);

      // Cleanup on unmount
      setTimeout(() => {
        clearInterval(pollInterval);
        if (qrPolling) {
          setQrPolling(false);
          setError('QR code expired. Please try again.');
        }
      }, 300000); // 5 minutes timeout

    } catch (err) {
      console.error('QR setup error:', err);
      setError(err.message || 'Failed to generate QR code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isSupported) {
    return (
      <div className="max-w-md mx-auto mt-8 p-6 bg-white rounded-lg shadow-md">
        <div className="text-center">
          <div className="text-red-600 text-4xl mb-4">⚠️</div>
          <h2 className="text-xl font-bold text-brown-800 mb-4">Browser Not Supported</h2>
          <p className="text-brown-600 mb-4">
            Your browser doesn't support passkeys. Please use a modern browser like:
          </p>
          <ul className="text-left text-brown-600 mb-4">
            <li>• Safari on iOS/macOS</li>
            <li>• Chrome on Android/Windows/Mac</li>
            <li>• Edge on Windows</li>
          </ul>
          <button
            onClick={() => navigate(-1)}
            className="bg-brown-600 hover:bg-brown-700 text-white font-bold py-2 px-4 rounded-full"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="max-w-md mx-auto mt-8 p-6 bg-white rounded-lg shadow-md">
        <div className="text-center">
          <div className="text-green-600 text-4xl mb-4">✓</div>
          <h2 className="text-xl font-bold text-green-800 mb-4">Passkey Set Up Successfully!</h2>
          <p className="text-brown-600 mb-4">
            You can now sign in with {hasPlatformAuth ? 'Face ID/Touch ID' : 'your passkey'}.
          </p>
          <p className="text-sm text-brown-500">Redirecting to profile...</p>
        </div>
      </div>
    );
  }

  // Show method selection first
  if (!setupMethod) {
    return (
      <div className="max-w-md mx-auto mt-8 p-6 bg-white rounded-lg shadow-md">
        <div className="text-center mb-6">
          <div className="text-4xl mb-4">🔐</div>
          <h2 className="text-2xl font-bold text-brown-800 mb-2">Add Passkey</h2>
          <p className="text-brown-600">Choose how you'd like to add your passkey</p>
        </div>

        <div className="space-y-4">
          {/* Option 1: This Device */}
          {hasPlatformAuth && (
            <button
              onClick={() => setSetupMethod('this-device')}
              className="w-full bg-white border-2 border-brown-300 hover:border-brown-600 rounded-lg p-4 text-left transition-all"
            >
              <div className="flex items-start">
                <div className="text-3xl mr-4">💻</div>
                <div className="flex-1">
                  <h3 className="font-bold text-brown-800 mb-1">Use This Device</h3>
                  <p className="text-sm text-brown-600">
                    Use Face ID, Touch ID, or Windows Hello on this computer
                  </p>
                </div>
              </div>
            </button>
          )}

          {/* Option 2: Phone via QR Code */}
          <button
            onClick={() => setSetupMethod('qr-code')}
            className="w-full bg-white border-2 border-brown-300 hover:border-brown-600 rounded-lg p-4 text-left transition-all"
          >
            <div className="flex items-start">
              <div className="text-3xl mr-4">📱</div>
              <div className="flex-1">
                <h3 className="font-bold text-brown-800 mb-1">Use Your Phone</h3>
                <p className="text-sm text-brown-600">
                  Scan QR code with your phone to add passkey
                </p>
              </div>
            </div>
          </button>
        </div>

        <div className="mt-6 text-center">
          <button
            onClick={() => navigate(-1)}
            className="text-brown-600 hover:text-brown-800 text-sm font-medium"
          >
            ← Back to Profile
          </button>
        </div>
      </div>
    );
  }

  // Show QR code if selected
  if (setupMethod === 'qr-code') {
    return (
      <div className="max-w-md mx-auto mt-8 p-6 bg-white rounded-lg shadow-md">
        <div className="text-center mb-6">
          <div className="text-4xl mb-4">📱</div>
          <h2 className="text-2xl font-bold text-brown-800 mb-2">Scan with Your Phone</h2>
          <p className="text-brown-600">Use your phone to add a passkey</p>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 text-sm">
            {error}
          </div>
        )}

        {!qrData ? (
          <div className="text-center">
            <button
              onClick={handleQRSetup}
              disabled={loading}
              className="bg-brown-600 hover:bg-brown-700 text-white font-bold py-3 px-6 rounded-full disabled:opacity-50"
            >
              {loading ? 'Generating QR Code...' : 'Generate QR Code'}
            </button>
          </div>
        ) : (
          <>
            <div className="bg-white border-4 border-brown-600 rounded-lg p-4 mb-4 flex justify-center">
              <QRCode value={qrData} size={200} level="H" />
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <h3 className="font-bold text-blue-800 mb-2 text-sm">📱 How to scan:</h3>
              <ol className="text-sm text-blue-700 space-y-1">
                <li>1. Open your phone's camera</li>
                <li>2. Point it at the QR code above</li>
                <li>3. Tap the notification that appears</li>
                <li>4. Use Face ID/Touch ID on your phone</li>
              </ol>
            </div>

            {qrPolling && (
              <div className="text-center">
                <div className="inline-flex items-center space-x-2 text-brown-600">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-brown-600"></div>
                  <span className="text-sm">Waiting for phone...</span>
                </div>
              </div>
            )}
          </>
        )}

        <div className="mt-6 text-center">
          <button
            onClick={() => {
              setSetupMethod(null);
              setQrData(null);
              setQrPolling(false);
            }}
            className="text-brown-600 hover:text-brown-800 text-sm font-medium"
            disabled={qrPolling}
          >
            ← Choose Different Method
          </button>
        </div>
      </div>
    );
  }

  // Show this device setup
  return (
    <div className="max-w-md mx-auto mt-8 p-6 bg-white rounded-lg shadow-md">
      <div className="text-center mb-6">
        <div className="text-4xl mb-4">💻</div>
        <h2 className="text-2xl font-bold text-brown-800 mb-2">
          Set Up {hasPlatformAuth ? 'Face ID/Touch ID' : 'Passkey'}
        </h2>
        <p className="text-brown-600">
          {hasPlatformAuth
            ? 'Use your face or fingerprint to sign in securely.'
            : 'Set up a passkey for this device.'}
        </p>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 text-sm">
          {error}
        </div>
      )}

      <div className="mb-6">
        <label htmlFor="deviceName" className="block text-brown-700 text-sm font-bold mb-2">
          Device Name (Optional)
        </label>
        <input
          type="text"
          id="deviceName"
          value={deviceName}
          onChange={(e) => setDeviceName(e.target.value)}
          placeholder="e.g., My Laptop"
          className="shadow appearance-none border rounded w-full py-2 px-3 text-brown-700 leading-tight focus:outline-none focus:shadow-outline"
          disabled={loading}
        />
      </div>

      <div className="flex gap-3">
        <button
          onClick={() => setSetupMethod(null)}
          className="flex-1 bg-gray-300 hover:bg-gray-400 text-brown-800 font-bold py-2 px-4 rounded-full"
          disabled={loading}
        >
          Back
        </button>
        <button
          onClick={handleSetup}
          disabled={loading}
          className="flex-1 bg-brown-600 hover:bg-brown-700 text-white font-bold py-2 px-4 rounded-full disabled:opacity-50"
        >
          {loading ? 'Setting Up...' : 'Set Up'}
        </button>
      </div>
    </div>
  );
};

export default PasskeySetup;
